import { Link, useRouter, useRouterState } from '@tanstack/react-router';
import { Avatar, AvatarFallback } from '@upme/ui/components/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@upme/ui/components/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@upme/ui/components/sidebar';
import { SUPPORT_TELEGRAM_URL } from '@upme/view-logic';
import {
  Ban,
  Briefcase,
  CalendarDays,
  CalendarPlus,
  ChevronsUpDown,
  Coins,
  Columns3,
  CreditCard,
  Gauge,
  ImageIcon,
  Inbox,
  LogOut,
  MapPin,
  MapPinned,
  Megaphone,
  MessageCircle,
  MessageSquare,
  Newspaper,
  Rss,
  SlidersHorizontal,
  Sunrise,
  Tag,
  Tags,
  Users,
  Wrench,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  canAccessOrganizerBlocks,
  canAccessUserRegistry,
  canManageCities,
} from '../../features/auth/admin-capabilities';
import { useAuth } from '../../features/auth/auth-context';
import { isCrmOnlyWorkspace, isCrmTeamMember } from '../../features/auth/default-admin-home';
import { LanguageSwitcher } from '../../features/settings/language-switcher';

export function AppSidebar() {
  const { t } = useTranslation('shared');
  const auth = useAuth();
  const router = useRouter();
  const { location } = useRouterState();
  const { isMobile } = useSidebar();

  const isSuperAdmin = auth.adminRoles.includes('SUPERADMIN');

  const isNavItemVisible = (to: string) => {
    if (isSuperAdmin) return true;
    if (to === '/organizer-blocks') return canAccessOrganizerBlocks(auth.adminRoles);
    if (to === '/users') return canAccessUserRegistry(auth.adminRoles);
    if (to === '/cities') return canManageCities(auth.adminRoles);
    return true;
  };

  const showDashboard = isSuperAdmin;

  const navGroups = [
    ...(showDashboard
      ? [
          {
            label: t('sidebar.groups.overview'),
            items: [{ to: '/', label: t('sidebar.nav.home'), icon: Gauge }],
          },
        ]
      : []),
    {
      label: t('sidebar.groups.content'),
      items: [
        { to: '/events', label: t('sidebar.nav.events'), icon: CalendarDays },
        { to: '/event-categories', label: t('sidebar.nav.eventCategories'), icon: Tag },
        { to: '/places', label: t('sidebar.nav.places'), icon: MapPinned },
        { to: '/place-categories', label: t('sidebar.nav.placeCategories'), icon: Tags },
        { to: '/services', label: t('sidebar.nav.services'), icon: Briefcase },
        { to: '/service-categories', label: t('sidebar.nav.serviceCategories'), icon: Wrench },
        { to: '/news', label: t('sidebar.nav.news'), icon: Newspaper },
      ],
    },
    {
      label: t('sidebar.groups.import'),
      items: [
        { to: '/source-channels', label: t('sidebar.nav.sourceChannels'), icon: Rss },
        { to: '/extractor-config', label: t('sidebar.nav.extractorConfig'), icon: SlidersHorizontal },
        { to: '/import-queue', label: t('sidebar.nav.importQueue'), icon: Inbox },
        { to: '/organizer-blocks', label: t('sidebar.nav.organizerBlocks'), icon: Ban },
      ],
    },
    {
      label: t('sidebar.groups.community'),
      items: [
        { to: '/users', label: t('sidebar.nav.users'), icon: Users },
        { to: '/meeting-templates', label: t('sidebar.nav.meetingTemplates'), icon: CalendarPlus },
        { to: '/photo-moderation', label: t('sidebar.nav.photoModeration'), icon: ImageIcon },
      ],
    },
    {
      label: t('sidebar.groups.crm'),
      items: [
        { to: '/crm', label: t('sidebar.nav.crmBoard'), icon: Columns3 },
        { to: '/crm/conversations', label: t('sidebar.nav.crmConversations'), icon: MessageSquare },
        { to: '/crm/telegram', label: t('sidebar.nav.crmTelegram'), icon: MessageCircle },
        ...(isSuperAdmin
          ? [{ to: '/crm/instagram' as const, label: t('sidebar.nav.crmInstagram'), icon: MessageCircle }]
          : []),
      ],
    },
    {
      label: t('sidebar.groups.monetization'),
      items: [
        { to: '/promotion-tasks', label: t('sidebar.nav.promotionTasks'), icon: Megaphone },
        { to: '/coin-transactions', label: t('sidebar.nav.coinTransactions'), icon: Coins },
        { to: '/payment-providers', label: t('sidebar.nav.paymentProviders'), icon: CreditCard },
      ],
    },
    {
      label: t('sidebar.groups.system'),
      items: [
        { to: '/cities', label: t('sidebar.nav.cities'), icon: MapPin },
        { to: '/morning-digest', label: t('sidebar.nav.morningDigest'), icon: Sunrise },
      ],
    },
  ] as const;

  const crmOnly = isCrmOnlyWorkspace(auth.adminRoles);
  const crmGroupLabel = t('sidebar.groups.crm');
  const systemGroupLabel = t('sidebar.groups.system');
  const visibleNavGroups = crmOnly
    ? navGroups
        .filter((g) => g.label === crmGroupLabel || g.label === systemGroupLabel)
        .map((g) =>
          g.label === systemGroupLabel
            ? { ...g, items: g.items.filter((item) => item.to === '/morning-digest') }
            : g,
        )
        .filter((g) => g.items.length > 0)
    : isCrmTeamMember(auth.adminRoles)
      ? navGroups
      : navGroups.filter((g) => g.label !== crmGroupLabel);

  const handleLogout = async () => {
    await auth.logout();
    router.navigate({ to: '/login' });
  };

  const user = auth.user;
  const initials = user?.firstName?.[0]?.toUpperCase() ?? user?.username?.[0]?.toUpperCase() ?? 'U';
  const displayName = user
    ? [user.firstName, user.lastName].filter(Boolean).join(' ') || user.username || t('sidebar.account')
    : t('sidebar.account');

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <div className="flex items-center gap-2 px-2 py-1.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground font-semibold">
            U
          </div>
          <span className="font-semibold group-data-[collapsible=icon]:hidden">{t('sidebar.brand')}</span>
        </div>
      </SidebarHeader>
      <SidebarContent>
        {visibleNavGroups.map((group) => (
          <SidebarGroup key={group.label}>
            <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items
                  .filter((item) => isNavItemVisible(item.to))
                  .map((item) => {
                    const isActive =
                      item.to === '/'
                        ? location.pathname === '/'
                        : location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
                    return (
                      <SidebarMenuItem key={item.to}>
                        <SidebarMenuButton asChild isActive={isActive} tooltip={item.label}>
                          <Link to={item.to}>
                            <item.icon />
                            <span>{item.label}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem className="px-2 py-1 group-data-[collapsible=icon]:hidden">
            <LanguageSwitcher />
          </SidebarMenuItem>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">{displayName}</span>
                    {user?.username && (
                      <span className="truncate text-xs text-muted-foreground">@{user.username}</span>
                    )}
                  </div>
                  <ChevronsUpDown className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side={isMobile ? 'bottom' : 'right'}
                align="end"
                sideOffset={4}
                className="min-w-56"
              >
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-medium">{displayName}</span>
                      {user?.username && (
                        <span className="truncate text-xs text-muted-foreground">@{user.username}</span>
                      )}
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <a href={SUPPORT_TELEGRAM_URL} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="mr-2 h-4 w-4" />
                    {t('support.writeUs')}
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  {t('sidebar.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
